import { cn } from '@/lib/utils';

export interface ServingLayoutInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  invalid?: boolean;
}

// Figma "Value input": one bordered box, grey label left, value right-aligned.
const BOX_CLASS =
  'flex cursor-text items-center gap-2 rounded-input border border-grey-light bg-off-white px-6 py-4 font-inter text-sm shadow-field';
const INVALID_BOX_CLASS = 'border-chilli-red-light bg-chilli-blush';

// Borderless, padding-less input that fills the rest of the box — no native
// border/outline, no spin buttons, so the box reads as a single labelled field.
const INPUT_CLASS = cn(
  'min-w-0 flex-1 border-0 bg-transparent p-0 text-right text-off-black placeholder:text-grey-light',
  'outline-none focus:outline-none focus:ring-0',
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
);

// Lines/Ways are non-negative whole-number counts with no "unset" state, so
// empty parses to 0 (the payload mapper drops zeros). `parseInt` + `Math.max`
// keep the form state a non-negative integer even when the HTML `step`/`min`
// constraints are bypassed (paste, manual edit), and the guard keeps NaN from
// non-numeric input out of the form state.
function parseCount(raw: string): number {
  if (raw === '') return 0;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
}

export function ServingLayoutInput({ label, value, onChange, invalid = false }: ServingLayoutInputProps) {
  return (
    <label className={cn(BOX_CLASS, invalid && INVALID_BOX_CLASS)}>
      <span className="shrink-0 text-grey">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        aria-label={label}
        aria-invalid={invalid || undefined}
        placeholder="1"
        value={value === 0 ? '' : value}
        onChange={(event) => onChange(parseCount(event.target.value))}
        className={INPUT_CLASS}
      />
    </label>
  );
}
